<template>
  <MainLayout>
    <template #header>
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-bold mb-1">
            {{ customer?.business_name || 'Customer Profile' }}
          </h1>
          <div v-if="customer" class="text-gray-600">
            {{ customer.first_name }} {{ customer.last_name }} - {{ customer.job_title }}
          </div>
        </div>
        <div class="flex gap-2">
          <Button 
            icon="pi pi-pencil" 
            label="Edit Customer" 
            class="p-button-outlined"
            @click="openEditModal"
          />
          <Button icon="pi pi-arrow-left" label="Back to Customers" class="p-button-outlined"
            @click="router.push({ name: 'Customers' })"
          />
        </div>
      </div>
    </template>

    <div v-if="loading" class="flex justify-center items-center h-64">
      <ProgressSpinner />
    </div>

    <div v-else-if="!customer" class="text-center p-8">
      <div class="text-xl text-red-500 mb-4">Customer not found</div>
      <Button
        label="Return to Customers"
        @click="router.push({ name: 'Customers' })"
      />
    </div>

    <div v-else>
      <TabView v-model:activeIndex="activeTab">
        <TabPanel header="Customer Info">
          <Card class="mb-4">
            <template #title>
              <div class="flex justify-between items-center">
                <span>Contact Information</span>
                <Button 
                  icon="pi pi-pencil" 
                  label="Edit Details" 
                  class="p-button-sm p-button-outlined"
                  @click="openEditModal"
                />
              </div>
            </template>
            <template #content>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="mb-2 text-sm font-semibold text-gray-500">Business</div>
                  <div class="mb-4">{{ customer.business_name || 'N/A' }}</div>
                  
                  <div class="mb-2 text-sm font-semibold text-gray-500">Name</div>
                  <div class="mb-4">{{ customer.title }} {{ customer.first_name }} {{ customer.last_name }}</div>
                </div>
                
                <div>
                  <div class="mb-2 text-sm font-semibold text-gray-500">Job Title</div>
                  <div class="mb-4">{{ customer.job_title || 'N/A' }}</div>
                  
                  <div class="mb-2 text-sm font-semibold text-gray-500">Email</div>
                  <div class="mb-4">
                    <a :href="`mailto:${customer.email}`" class="text-blue-600 hover:underline">
                      {{ customer.email }}
                    </a>
                  </div>
                  
                  <div class="mb-2 text-sm font-semibold text-gray-500">Phone</div>
                  <div class="mb-4">
                    <a v-if="customer.phone_number" :href="`tel:${customer.phone_number}`" class="text-blue-600 hover:underline">
                      {{ customer.phone_number }}
                    </a>
                    <span v-else>N/A</span>
                  </div>

                  <div v-if="customer.alldoq_instance?.subdomain" class="mb-2 text-sm font-semibold text-gray-500">Customer Type</div>
                  <div v-if="customer.alldoq_instance?.subdomain" class="mb-4">
                    <Tag :severity="getCustomerTypeSeverity(displayedCustomerType)" class="mr-2">
                      {{ getCustomerTypeLabel(displayedCustomerType) }}
                    </Tag>
                    <small class="text-gray-500 ml-2">{{ getCustomerTypeDescription(displayedCustomerType) }}</small>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </TabPanel>

        <TabPanel header="ALLDOQ Instance">
          <div v-if="customer.alldoq_instance">
            <AllDoqInstanceDetails 
              :customerId="customer.id" 
              :customerName="`${customer.first_name} ${customer.last_name}`" 
              :instanceUrl="`https://${customer.alldoq_instance.subdomain}.alldoq.com`"
              :instanceStatus="customer.alldoq_instance.status"
              :tenant="customer.alldoq_instance.subdomain"
              :instanceId="customer.alldoq_instance.id"
            />
          </div>
          
          <div v-else class="p-4 text-center border rounded-lg bg-gray-50">
            <div class="text-lg mb-2">No AllDoq Instance</div>
            <div class="text-gray-600 mb-4">This customer doesn't have an AllDoq instance yet.</div>
          </div>
        </TabPanel>

        <TabPanel header="Installation">
          <Card class="mb-4" v-if="customer.alldoq_installation">
            <template #title>Installation Details</template>
            <template #content>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Installation details would go here -->
                <div>
                  <div class="mb-2 text-sm font-semibold text-gray-500">Status</div>
                  <div class="mb-4">
                    <Tag :severity="getInstallationSeverity(customer.alldoq_installation.status)">
                      {{ customer.alldoq_installation.status }}
                    </Tag>
                  </div>
                </div>
              </div>
            </template>
          </Card>
          
          <div v-else class="p-4 text-center border rounded-lg bg-gray-50">
            <div class="text-lg mb-2">No Installation Information</div>
            <div class="text-gray-600 mb-4">No installation details available for this customer.</div>
          </div>
        </TabPanel>
      </TabView>
    </div>
    
    <!-- Edit Customer Modal -->
    <Dialog 
      v-model:visible="editModalVisible" 
      header="Edit customer details" 
      :style="{ width: '500px' }" 
      :modal="true"
      :closable="true"
    >
      <div class="p-fluid">
        <div class="field mb-4">
          <label for="title" class="block mb-2">Title</label>
          <Dropdown
            id="title"
            v-model="editForm.title"
            :options="titleOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Title"
            class="w-full"
            :class="{ 'p-invalid': formErrors.title }"
          />
          <small v-if="formErrors.title" class="p-error">{{ formErrors.title }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="first_name" class="block mb-2">First Name</label>
          <InputText 
            id="first_name" 
            v-model="editForm.first_name" 
            class="w-full"
            :class="{ 'p-invalid': formErrors.first_name }"
          />
          <small v-if="formErrors.first_name" class="p-error">{{ formErrors.first_name }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="last_name" class="block mb-2">Last Name</label>
          <InputText 
            id="last_name" 
            v-model="editForm.last_name" 
            class="w-full"
            :class="{ 'p-invalid': formErrors.last_name }"
          />
          <small v-if="formErrors.last_name" class="p-error">{{ formErrors.last_name }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="phone_number" class="block mb-2">Phone Number</label>
          <InputText 
            id="phone_number" 
            v-model="editForm.phone_number" 
            class="w-full"
            :class="{ 'p-invalid': formErrors.phone_number }"
          />
          <small v-if="formErrors.phone_number" class="p-error">{{ formErrors.phone_number }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="email" class="block mb-2">Email</label>
          <InputText
            id="email"
            v-model="editForm.email"
            class="w-full"
            :class="{ 'p-invalid': formErrors.email }"
          />
          <small v-if="formErrors.email" class="p-error">{{ formErrors.email }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="job_title" class="block mb-2">Job Title</label>
          <InputText 
            id="job_title" 
            v-model="editForm.job_title" 
            class="w-full"
            :class="{ 'p-invalid': formErrors.job_title }"
          />
          <small v-if="formErrors.job_title" class="p-error">{{ formErrors.job_title }}</small>
        </div>
        
        <div class="field mb-4">
          <label for="business_name" class="block mb-2">Business Name</label>
          <InputText 
            id="business_name" 
            v-model="editForm.business_name" 
            class="w-full"
            :class="{ 'p-invalid': formErrors.business_name }"
          />
          <small v-if="formErrors.business_name" class="p-error">{{ formErrors.business_name }}</small>
        </div>

        <div class="field mb-4" v-if="customer?.alldoq_instance?.subdomain">
          <label for="customer_type" class="block mb-2">Customer Type</label>
          <Dropdown
            id="customer_type"
            v-model="editForm.customer_type"
            :options="customerTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Customer Type"
            class="w-full"
            :class="{ 'p-invalid': formErrors.customer_type }"
            :loading="customerTypeLoading"
          />
          <small v-if="formErrors.customer_type" class="p-error">{{ formErrors.customer_type }}</small>
          <small class="text-gray-600 mt-1 block">
            This affects which features and workflows are available in the ALLDOQ instance.
          </small>
        </div>
      </div>
      
      <template #footer>
        <Button 
          label="Cancel" 
          icon="pi pi-times" 
          class="p-button-text" 
          @click="cancelEdit"
        />
        <Button 
          label="Save" 
          icon="pi pi-check" 
          :loading="saving" 
          @click="saveCustomer"
        />
      </template>
    </Dialog>
  </MainLayout>
</template>

<script setup>
import { ref, onMounted, watch, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '../store/auth';
import customerService from '../utils/customerService';
import MainLayout from '../components/MainLayout.vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import AllDoqInstanceDetails from '../components/AllDoqInstanceDetails.vue';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();

// Data
const customer = ref(null);
const loading = ref(true);
const activeTab = ref(0);
const editModalVisible = ref(false);
const saving = ref(false);
const editForm = reactive({
  title: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  email: '',
  job_title: '',
  business_name: '',
  customer_type: ''
});
const formErrors = reactive({});
const customerTypeLoading = ref(false);

const titleOptions = [
  { label: 'Mr', value: 'Mr' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Miss', value: 'Miss' },
  { label: 'Ms', value: 'Ms' },
  { label: 'Dr', value: 'Dr' },
  { label: 'Prof', value: 'Prof' }
];

const customerTypeOptions = [
  { label: 'Law Firm', value: 'law_firm' },
  { label: 'Agency', value: 'agency' },
  { label: 'Expert Witness', value: 'expert_witness' }
];

// Reactive customer type for display
const displayedCustomerType = ref('expert_witness');

// Methods
const loadCustomer = async () => {
  loading.value = true;
  try {
    const response = await customerService.getCustomer(route.params.id);
    customer.value = response.data;
    console.log('Customer data loaded:', customer.value);
    
    // Load customer type if customer has ALLDOQ instance
    if (customer.value?.alldoq_instance?.subdomain) {
      displayedCustomerType.value = await fetchCurrentCustomerType(customer.value.alldoq_instance.subdomain);
    }
  } catch (error) {
    console.error('Error loading customer:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load customer details',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

// Helper functions for customer type display
const getCustomerTypeLabel = (value) => {
  const type = customerTypeOptions.find(t => t.value === value);
  return type ? type.label : 'Expert Witness';
};

const getCustomerTypeSeverity = (value) => {
  switch (value) {
    case 'law_firm':
      return 'success';
    case 'agency':
      return 'info';
    case 'expert_witness':
      return 'warning';
    default:
      return 'warning';
  }
};

const getCustomerTypeDescription = (value) => {
  switch (value) {
    case 'law_firm':
      return 'Full legal features with case creation wizard';
    case 'agency':
      return 'Standard case management features';
    case 'expert_witness':
      return 'Basic features for expert testimony';
    default:
      return 'Basic features for expert testimony';
  }
};

const fetchCurrentCustomerType = async (tenant) => {
  if (!tenant) return 'expert_witness'; // default
  
  customerTypeLoading.value = true;
  try {
    const response = await fetch(`/api/v1/accounts_proxy/${tenant}/plan_settings`);
    if (response.ok) {
      const data = await response.json();
      // The customer type might be in the account data, but if not available, 
      // we'll try to fetch it from a different endpoint or default to expert_witness
      return data.customer_type || 'expert_witness';
    }
  } catch (error) {
    console.error('Error fetching customer type:', error);
  } finally {
    customerTypeLoading.value = false;
  }
  return 'expert_witness'; // default fallback
};

const openEditModal = async () => {
  console.log('Opening edit modal, customer data:', customer.value);
  // Populate form with current customer data
  if (customer.value) {
    editForm.title = customer.value.title;
    editForm.first_name = customer.value.first_name;
    editForm.last_name = customer.value.last_name;
    editForm.phone_number = customer.value.phone_number;
    editForm.email = customer.value.email;
    editForm.job_title = customer.value.job_title;
    editForm.business_name = customer.value.business_name;
    
    // Fetch current customer type if customer has an ALLDOQ instance
    if (customer.value.alldoq_instance?.subdomain) {
      editForm.customer_type = await fetchCurrentCustomerType(customer.value.alldoq_instance.subdomain);
    }
  } else {
    console.error('Cannot open edit modal: customer data is not available');
  }
  
  // Clear any previous errors
  Object.keys(formErrors).forEach(key => delete formErrors[key]);
  
  // Show modal
  editModalVisible.value = true;
  console.log('Modal visibility set to:', editModalVisible.value);
};

const cancelEdit = () => {
  editModalVisible.value = false;
};

const validateForm = () => {
  let isValid = true;
  Object.keys(formErrors).forEach(key => delete formErrors[key]);
  
  if (!editForm.title) {
    formErrors.title = 'Title is required';
    isValid = false;
  }
  
  if (!editForm.first_name) {
    formErrors.first_name = 'First name is required';
    isValid = false;
  }
  
  if (!editForm.last_name) {
    formErrors.last_name = 'Last name is required';
    isValid = false;
  }
  
  if (!editForm.phone_number) {
    formErrors.phone_number = 'Phone number is required';
    isValid = false;
  } else if (!/^\+?[\d\s\-\(\)]+$/.test(editForm.phone_number)) {
    formErrors.phone_number = 'Please enter a valid phone number';
    isValid = false;
  }
  
  if (!editForm.email) {
    formErrors.email = 'Email is required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
    formErrors.email = 'Please enter a valid email address';
    isValid = false;
  }
  
  if (!editForm.job_title) {
    formErrors.job_title = 'Job title is required';
    isValid = false;
  }
  
  if (!editForm.business_name) {
    formErrors.business_name = 'Business name is required';
    isValid = false;
  }
  
  return isValid;
};

const saveCustomer = async () => {
  if (!validateForm()) {
    return;
  }
  
  saving.value = true;
  try {
    // Update customer details
    const response = await customerService.updateCustomer(customer.value.id, {
      title: editForm.title,
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      phone_number: editForm.phone_number,
      email: editForm.email,
      job_title: editForm.job_title,
      business_name: editForm.business_name
    });
    
    // Update customer type if customer has ALLDOQ instance and type was changed
    if (customer.value.alldoq_instance?.subdomain && editForm.customer_type) {
      const originalCustomerType = await fetchCurrentCustomerType(customer.value.alldoq_instance.subdomain);
      
      if (editForm.customer_type !== originalCustomerType) {
        try {
          const customerTypeResponse = await fetch(`/api/v1/accounts_proxy/${customer.value.alldoq_instance.subdomain}/customer_type`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              customer_type: editForm.customer_type
            })
          });
          
          if (!customerTypeResponse.ok) {
            throw new Error('Failed to update customer type');
          }
          
          console.log('Customer type updated successfully');
          // Update the displayed customer type
          displayedCustomerType.value = editForm.customer_type;
        } catch (customerTypeError) {
          console.error('Error updating customer type:', customerTypeError);
          toast.add({
            severity: 'warn',
            summary: 'Partial Update',
            detail: 'Customer details updated, but failed to update customer type. Please try again.',
            life: 5000
          });
        }
      }
    }
    
    // Update the local customer data
    customer.value = response.data;
    
    // Close the modal
    editModalVisible.value = false;
    
    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Customer details updated successfully',
      life: 3000
    });
  } catch (error) {
    console.error('Error updating customer:', error);
    
    // Check for validation errors from backend
    if (error.response && error.response.data && error.response.data.errors) {
      error.response.data.errors.forEach(err => {
        const field = err.toLowerCase().split(' ')[0];
        formErrors[field] = err;
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update customer details',
        life: 3000
      });
    }
  } finally {
    saving.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const getInstanceSeverity = (status) => {
  if (!status) return 'danger';
  
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'inactive':
      return 'danger';
    default:
      return 'info';
  }
};

const getInstallationSeverity = (status) => {
  if (!status) return 'danger';
  
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'info';
  }
};

// Lifecycle
onMounted(async () => {
  console.log('Component mounted, auth state:', authStore.isAuthenticated);
  console.log('Route params:', route.params);
  
  if (authStore.isAuthenticated) {
    await loadCustomer();
    
    // Set active tab based on query parameter
    if (route.query.tab === 'alldoq') {
      activeTab.value = 1; // Index of the AllDoq Instance tab
    }
  } else {
    console.log('Not authenticated, redirecting to login');
    router.push({ name: 'Login' });
  }
});

// Watch for auth state changes
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      loadCustomer();
    }
  }
);

// Watch for route params changes
watch(
  () => route.params.id,
  (newId) => {
    if (newId && authStore.isAuthenticated) {
      loadCustomer();
    }
  }
);

// Watch for tab changes in the URL
watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab === 'alldoq') {
      activeTab.value = 1; // Index of the AllDoq Instance tab
    }
  }
);
</script>